import { useRef, useEffect, useState, useCallback } from "react";
import type { Dispatch, SetStateAction } from "react";
import { Send, Paperclip, X, FileText, Mic, MicOff } from "lucide-react";
import { formatBytes } from "../../utils";
import type { ChatCsv, ChatImage } from "../../types/chat";

// Web Speech API — not yet in TypeScript's lib, so declare minimally here
interface ISpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  onresult: ((e: SpeechRecognitionEvent) => void) | null;
  onerror: ((e: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
}
interface SpeechRecognitionEvent extends Event {
  resultIndex: number;
  results: SpeechRecognitionResultList;
}
interface SpeechRecognitionErrorEvent extends Event {
  error: string;
}
declare global {
  interface Window {
    SpeechRecognition?: new () => ISpeechRecognition;
    webkitSpeechRecognition?: new () => ISpeechRecognition;
  }
}

function createRecognition(): ISpeechRecognition | null {
  const Ctor = window.SpeechRecognition ?? window.webkitSpeechRecognition;
  if (!Ctor) return null;
  const r = new Ctor();
  r.continuous = false;
  r.interimResults = true;
  r.lang = "en-US";
  return r;
}

const MAX_IMAGES = 5;
const MAX_IMAGE_BYTES = 5 * 1024 * 1024; // 5 MB
const MAX_CSVS = 3;
const MAX_CSV_BYTES = 10 * 1024 * 1024; // 10 MB

interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  disabled?: boolean;
  images?: ChatImage[];
  onImagesChange: Dispatch<SetStateAction<ChatImage[]>>;
  csvs?: ChatCsv[];
  onCsvsChange: Dispatch<SetStateAction<ChatCsv[]>>;
}

export default function ChatInput({ value, onChange, onSend, disabled, images = [], onImagesChange, csvs = [], onCsvsChange }: ChatInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);

  // Voice input
  const recognitionRef = useRef<ISpeechRecognition | null>(null);
  const [isListening, setIsListening] = useState(false);
  const [micError, setMicError] = useState<string | null>(null);
  const interimRef = useRef(""); // tracks the interim (partial) transcript
  const baseValueRef = useRef(""); // value snapshot when recording started
  const isSpeechSupported = typeof window !== "undefined" && !!(window.SpeechRecognition ?? window.webkitSpeechRecognition);

  const stopListening = useCallback(() => {
    recognitionRef.current?.stop();
    recognitionRef.current = null;
    interimRef.current = "";
    setIsListening(false);
  }, []);

  function toggleMic() {
    if (isListening) { stopListening(); return; }
    setMicError(null);
    const recognition = createRecognition();
    if (!recognition) return;
    recognitionRef.current = recognition;
    baseValueRef.current = value;
    interimRef.current = "";

    recognition.onresult = (e: SpeechRecognitionEvent) => {
      let interim = "";
      let finalChunk = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalChunk += t;
        else interim += t;
      }
      interimRef.current = interim;
      // Show base + any confirmed text accumulated so far + current interim
      const confirmed = value.startsWith(baseValueRef.current)
        ? value.slice(baseValueRef.current.length).replace(interimRef.current, "")
        : "";
      onChange(baseValueRef.current + (baseValueRef.current && (confirmed + finalChunk + interim) ? " " : "") + confirmed + finalChunk + interim);
      if (finalChunk) {
        baseValueRef.current = value.trimEnd() + (value.trimEnd() ? " " : "") + finalChunk.trim();
        interimRef.current = "";
      }
    };

    recognition.onerror = (e: SpeechRecognitionErrorEvent) => {
      if (e.error !== "aborted") setMicError(e.error === "not-allowed" ? "Microphone access denied." : "Voice input error.");
      stopListening();
    };

    recognition.onend = () => {
      interimRef.current = "";
      setIsListening(false);
      recognitionRef.current = null;
      textareaRef.current?.focus();
    };

    recognition.start();
    setIsListening(true);
  }

  // Clean up on unmount
  useEffect(() => () => { recognitionRef.current?.stop(); }, []);

  // Auto-resize textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(Math.max(el.scrollHeight, 40), 160)}px`;
  }, [value]);

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!disabled && (value.trim() || images.length || csvs.length)) onSend();
    }
  }

  function processFiles(files: FileList | File[]) {
    const imageFiles = Array.from(files).filter((f) => f.type.startsWith("image/"));
    const csvFiles = Array.from(files).filter((f) => f.name.endsWith(".csv") || f.type === "text/csv");

    const remainingImages = MAX_IMAGES - images.length;
    imageFiles
      .filter((f) => f.size <= MAX_IMAGE_BYTES)
      .slice(0, remainingImages)
      .forEach((file) => {
        const reader = new FileReader();
        reader.onload = (ev) => {
          const dataUrl = ev.target?.result as string;
          const [header, base64] = dataUrl.split(",");
          const mediaType = header.replace("data:", "").replace(";base64", "");
          onImagesChange((prev) => [...prev, { dataUrl, mediaType, base64 }]);
        };
        reader.readAsDataURL(file);
      });

    const remainingCsvs = MAX_CSVS - csvs.length;
    csvFiles
      .filter((f) => f.size <= MAX_CSV_BYTES)
      .slice(0, remainingCsvs)
      .forEach((file) => {
        const reader = new FileReader();
        reader.onload = (ev) => {
          onCsvsChange((prev) => [...prev, { filename: file.name, content: ev.target?.result as string, size: file.size }]);
        };
        reader.readAsText(file);
      });
  }

  function handleFiles(e: React.ChangeEvent<HTMLInputElement>) {
    processFiles(e.target.files || []);
    e.target.value = "";
  }

  function handleDragEnter(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    dragCounter.current += 1;
    if (dragCounter.current === 1) setIsDragging(true);
  }

  function handleDragLeave(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    dragCounter.current -= 1;
    if (dragCounter.current === 0) setIsDragging(false);
  }

  function handleDragOver(e: React.DragEvent<HTMLDivElement>) { e.preventDefault(); }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    dragCounter.current = 0;
    setIsDragging(false);
    if (disabled) return;
    processFiles(e.dataTransfer.files);
  }

  function removeImage(idx: number) {
    onImagesChange((prev) => prev.filter((_, i) => i !== idx));
  }

  function removeCsv(idx: number) {
    onCsvsChange((prev) => prev.filter((_, i) => i !== idx));
  }

  const canSend = !disabled && (value.trim().length > 0 || images.length > 0 || csvs.length > 0);
  const canAttach = !disabled && (images.length < MAX_IMAGES || csvs.length < MAX_CSVS);

  return (
    <div
      className="border-t border-gray-200 bg-white px-4 py-3"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Image preview strip */}
      {images.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {images.map((img, idx) => (
            <div key={idx} className="relative shrink-0">
              <img
                src={img.dataUrl}
                alt=""
                className="h-16 w-16 rounded-lg object-cover border border-gray-200"
              />
              <button
                onClick={() => removeImage(idx)}
                className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-gray-600 text-white hover:bg-gray-800"
              >
                <X size={9} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* CSV chip strip */}
      {csvs.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {csvs.map((csv, idx) => (
            <div key={idx} className="flex items-center gap-1.5 rounded-lg border border-green-200 bg-green-50 px-2.5 py-1.5 text-xs text-green-800">
              <FileText size={12} className="shrink-0 text-green-600" />
              <span className="max-w-[180px] truncate font-medium">{csv.filename}</span>
              <span className="text-green-500">{formatBytes(csv.size ?? 0)}</span>
              <button
                onClick={() => removeCsv(idx)}
                className="ml-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-green-200 hover:bg-green-300 text-green-700"
              >
                <X size={8} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className={`flex items-center gap-2 rounded-xl border bg-white px-3 py-2 transition-colors ${
        isDragging
          ? "border-blue-400 bg-blue-50 ring-1 ring-blue-400"
          : "border-gray-300 focus-within:border-blue-400 focus-within:ring-1 focus-within:ring-blue-400"
      }`}>
        {/* Hidden file input — accepts images and CSVs */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,.csv,text/csv"
          multiple
          className="hidden"
          onChange={handleFiles}
        />

        {/* Attach button */}
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={!canAttach}
          title="Attach image or CSV"
          className="shrink-0 text-gray-400 hover:text-gray-600 disabled:opacity-30"
        >
          <Paperclip size={16} />
        </button>

        {/* Mic button — only rendered when browser supports Speech API */}
        {isSpeechSupported && (
          <button
            type="button"
            onClick={toggleMic}
            disabled={disabled}
            title={isListening ? "Stop recording" : "Speak your message"}
            className={`shrink-0 transition-colors disabled:opacity-30 ${
              isListening
                ? "text-red-500 hover:text-red-600 animate-pulse"
                : "text-gray-400 hover:text-gray-600"
            }`}
          >
            {isListening ? <MicOff size={16} /> : <Mic size={16} />}
          </button>
        )}

        <textarea
          ref={textareaRef}
          rows={1}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder={isDragging ? "Drop files here…" : "Ask anything about your data..."}
          className="min-h-10 flex-1 resize-none overflow-hidden bg-transparent py-2 text-sm leading-6 text-gray-800 placeholder-gray-400 focus:outline-none disabled:opacity-50"
        />
        <button
          onClick={onSend}
          disabled={!canSend}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-600 text-white transition-colors hover:bg-blue-700 disabled:opacity-40"
        >
          <Send size={14} />
        </button>
      </div>
      {micError && (
        <p className="mt-1 text-center text-[11px] text-red-500">{micError}</p>
      )}
      <p className="mt-1.5 text-center text-[11px] text-gray-400">
        Shift+Enter for new line · Enter to send · Attach or drop images and CSVs{isSpeechSupported ? " · Mic to speak" : ""}
      </p>
    </div>
  );
}
